import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { DEFAULT_TESTIMONIALS } from "../siteContext";
import { useLang, pick } from "../i18n";
import { SectionBackdrop } from "./SectionBackdrop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Testimonials = () => {
  const { lang, s } = useLang();
  const [quotes, setQuotes] = useState(DEFAULT_TESTIMONIALS);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setQuotes(res.data);
      })
      .catch(() => {});
  }, []);

  return (
  <section id="testimonials" data-testid="testimonials-section" className="relative py-28 md:py-36 bg-[var(--c-light-bg,#F8F6F0)] border-b border-[var(--c-light-border,#E8E2D8)]">
    <SectionBackdrop id="testimonials" tone="light" />
    <div className="relative max-w-7xl mx-auto px-4 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="text-center max-w-3xl mx-auto mb-16"
      >
        <p className="text-[#A8823F] text-sm tracking-[0.35em] mb-6">{s.testi_eyebrow}</p>
        <h2 data-testid="testimonials-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-[var(--c-light-text,#1A102F)] leading-[1.25]">
          {s.testi_title_a} <span className="text-[#A8823F] italic">{s.testi_title_b}</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {quotes.map((q, i) => (
          <motion.blockquote
            key={q.id || i}
            data-testid={`testimonial-card-${i}`}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: i * 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative border border-[var(--c-light-border,#E8E2D8)] bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition-all duration-500 flex flex-col"
          >
            <Quote className="w-10 h-10 text-[#D1A05C] mb-6" strokeWidth={0.75} />
            <p className="font-serif text-xl leading-relaxed text-[var(--c-light-text,#1A102F)] mb-8 flex-1" dir="auto">{pick(lang, q, "text")}</p>
            <footer>
              <p className="text-[#A8823F] text-sm font-semibold">{pick(lang, q, "name")}</p>
              <p className="text-[var(--c-light-muted,#605670)] text-xs mt-1">{pick(lang, q, "role")}</p>
            </footer>
          </motion.blockquote>
        ))}
      </div>

      <div data-testid="testimonials-pagination-dots" className="mt-12 flex items-center justify-center gap-2.5">
        {quotes.map((_, i) => (
          <span
            key={i}
            className={`h-2 rounded-full transition-all duration-500 ${i === 0 ? "w-6 bg-[#D1A05C]" : "w-2 bg-[#1A102F]/20"}`}
          />
        ))}
      </div>
    </div>
  </section>
  );
};
